/**
 * Degradado por tiempo de una foto que quedó esperando el veredicto del análisis.
 *
 * **Qué problema resuelve.** Una foto sube bien, pasa el control de calidad y espera el veredicto de
 * la IA. Si ese veredicto no llega —hoy el único emisor del caso positivo es el canal de IA, porque
 * la Lambda publica **sólo** errores por diseño— la foto queda en `pending` **para siempre**: se
 * muestra borrosa, sin texto, y no se puede borrar. No es un bug de un componente: es un hueco entre
 * dos servicios que no vive en ninguno de los dos.
 *
 * **Qué NO hace: bloquear.** La matriz del enunciado pedía que `pending` trabara la finalización, y
 * el autor lo superseció en el comentario del 2026-08-25: la única condición que bloquea es que
 * falte una foto obligatoria, y la calidad queda **sólo como advertencia**. Este degradado es
 * justamente lo que convierte una foto muda y permanente en la advertencia que ese comentario pide.
 *
 * **Por qué el plazo se mide contra `timestamp` y no contra un contador de sesión.** El `timestamp`
 * del archivo se persiste en IndexedDB al subirlo, así que el plazo es **absoluto**: recargar la app
 * o cerrarla y volver no lo reinicia. Un contador de sesión dejaría la foto atrapada igual, porque
 * cada reapertura empezaría de cero justo en el caso que este módulo existe para resolver.
 */
import { environment } from '../../environments/environment';

/**
 * Cuánto espera una foto el veredicto antes de degradarse a `analysis_failed`. Se configura por
 * ambiente en `src/environments/environment*.ts` y no acá: la guarda del Gateway lo lee de cada uno
 * para compararlo con su propio plazo de análisis.
 */
export const PENDING_TIMEOUT_MS = environment.analysis.pendingTimeoutMs;

/**
 * Texto del estado degradado.
 *
 * **Espeja `PhotoAnalysisManager.ts:44-45` del API interno** (`let_autoinspeccion_seguros_nodejs`),
 * que es el que publica `analysis_failed` con este mismo mensaje cuando el análisis falla de verdad.
 * Se repite el literal a propósito para que el asegurado vea **lo mismo** si el servidor avisa que si
 * expira el plazo.
 *
 * **Son dos literales que deben coincidir y van a divergir** el día que alguien cambie uno de los
 * dos: no hay nada que los compare, porque el texto viaja dentro de un evento y no de un contrato.
 * `ai_texts` no sirve como origen único — trae cuatro textos (`success`, `error`, `corrected`,
 * `pending`) y ninguno es éste.
 */
export const ANALYSIS_FAILED_MESSAGE = 'Nuestra IA no pudo analizar tu foto';

/** Lo mínimo que hace falta de un archivo de la tabla `Photo`. */
export interface DegradableFile {
  timestamp?: number;
  analysisResult?: { status?: string; messages?: string[] };
}

export interface DegradableEntry {
  photoId: number;
  files: DegradableFile[];
}

export interface Degradation {
  photoId: number;
  fileIdx: number;
  /** Milisegundos que la foto llevaba esperando. Sirve para el log y para 426. */
  waitedMs: number;
}

/**
 * Qué archivos hay que degradar. **No muta nada**: devuelve la lista y el llamador persiste.
 *
 * Sólo alcanza a `pending`. Un `success`, un `error` o un `corrected` ya tienen veredicto y el plazo
 * no les aplica; un archivo sin `timestamp` tampoco, porque sin él no hay plazo que medir y suponer
 * uno degradaría fotos recién subidas.
 */
export function findStalePending(
  entries: DegradableEntry[],
  nowMs: number,
  timeoutMs: number = PENDING_TIMEOUT_MS,
): Degradation[] {
  const stale: Degradation[] = [];
  for (const entry of entries) {
    entry.files.forEach((file, fileIdx) => {
      if (file.analysisResult?.status !== 'pending') {
        return;
      }
      if (typeof file.timestamp !== 'number' || !Number.isFinite(file.timestamp)) {
        return;
      }
      const waitedMs = nowMs - file.timestamp;
      if (waitedMs >= timeoutMs) {
        stale.push({ photoId: entry.photoId, fileIdx, waitedMs });
      }
    });
  }
  return stale;
}

/**
 * Cuánto falta para el próximo degradado, o `null` si no hay ninguno esperando.
 *
 * Existe para poder programar **un** temporizador al vencimiento exacto en vez de sondear. Devuelve
 * 0 cuando ya hay alguno vencido, así que el llamador puede tratar los dos casos igual.
 */
export function msUntilNextDegradation(
  entries: DegradableEntry[],
  nowMs: number,
  timeoutMs: number = PENDING_TIMEOUT_MS,
): number | null {
  let soonest: number | null = null;
  for (const entry of entries) {
    for (const file of entry.files) {
      if (file.analysisResult?.status !== 'pending') {
        continue;
      }
      if (typeof file.timestamp !== 'number' || !Number.isFinite(file.timestamp)) {
        continue;
      }
      const remaining = Math.max(0, file.timestamp + timeoutMs - nowMs);
      if (soonest === null || remaining < soonest) {
        soonest = remaining;
      }
    }
  }
  return soonest;
}
